import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Upload, X, CheckCircle2, Home, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STEPS = ["Property", "Details", "Photos", "Contact"];

async function fileToBase64(file, maxSize = 1400) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.floor(width * ratio); height = Math.floor(height * ratio);
        }
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function QuotePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const productId = params.get("product") || "";
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [refProduct, setRefProduct] = useState(null);
  const [form, setForm] = useState({
    property_type: params.get("type") || "residential",
    address: "",
    suburb: "",
    num_cameras: 4,
    installation_type: "wired",
    preferred_date: "",
    notes: "",
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: "",
    images: [],
    product_id: productId,
  });

  useEffect(() => {
    if (!productId) return;
    api.get(`/products/${productId}`).then(r => setRefProduct(r.data)).catch(() => setRefProduct(null));
  }, [productId]);

  useEffect(() => {
    if (user) setForm(f => ({
      ...f,
      contact_name: f.contact_name || user.name || "",
      contact_email: f.contact_email || user.email || "",
    }));
  }, [user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onFiles = async (fileList) => {
    const arr = Array.from(fileList);
    const room = 10 - form.images.length;
    if (arr.length > room) toast.error(`You can upload up to 10 images (${room} slots left).`);
    const toAdd = arr.slice(0, room);
    try {
      const b64s = await Promise.all(toAdd.map(f => fileToBase64(f)));
      set("images", [...form.images, ...b64s]);
    } catch { toast.error("Couldn't read one of the images."); }
  };

  const canNext = () => {
    if (step === 0) return !!form.property_type;
    if (step === 1) return form.address.trim().length > 3 && form.num_cameras > 0;
    if (step === 3) return form.contact_name && form.contact_email && form.contact_phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      toast.success("Quote request sent! We'll email you within 24 hours.");
      nav(user ? "/dashboard" : "/");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12 md:py-16">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">Free · No obligation · 24h reply</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-2">Get Your Camera Quote</h1>
        <p className="text-muted-foreground">Tell us about your property. Our team replies with a visual camera plan and fixed price.</p>
      </div>

      {refProduct && (
        <div className="flex items-center gap-4 p-4 mb-8 rounded-2xl border border-eagle-gold/30 bg-eagle-gold/5" data-testid="quote-product-reference">
          <div className="h-14 w-14 rounded-lg bg-secondary overflow-hidden shrink-0">
            {refProduct.images?.[0] && <img src={refProduct.images[0]} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-eagle-gold font-semibold mb-0.5">Quoting for</div>
            <div className="font-display font-semibold truncate">{refProduct.name}</div>
            {refProduct.sku && <div className="text-xs text-muted-foreground mt-0.5">SKU: {refProduct.sku}</div>}
          </div>
          <button
            onClick={() => { setRefProduct(null); set("product_id", ""); }}
            className="shrink-0 h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center text-muted-foreground"
            aria-label="Remove product reference"
            data-testid="quote-product-reference-clear"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 transition-colors ${i <= step ? "bg-eagle-gold text-eagle-ink" : "bg-secondary text-muted-foreground"}`}>
              {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <div className={`text-sm font-medium hidden sm:block ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 transition-colors ${i < step ? "bg-eagle-gold" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      <Card className="p-6 md:p-10">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            {step === 0 && (
              <div>
                <div className="font-display text-2xl font-semibold mb-6">What type of property?</div>
                <RadioGroup value={form.property_type} onValueChange={(v) => set("property_type", v)} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { v: "residential", label: "Residential", icon: Home, d: "House, unit, townhouse" },
                    { v: "commercial", label: "Commercial", icon: Building2, d: "Business, warehouse, site" },
                  ].map(o => (
                    <label key={o.v} className={`p-6 border-2 rounded-2xl cursor-pointer transition-all ${form.property_type === o.v ? "border-eagle-gold bg-eagle-gold/5" : "border-border hover:border-muted-foreground/40"}`} data-testid={`property-type-${o.v}`}>
                      <RadioGroupItem value={o.v} className="sr-only" />
                      <o.icon className="h-8 w-8 text-eagle-gold mb-4" strokeWidth={1.5} />
                      <div className="font-display font-semibold text-lg mb-1">{o.label}</div>
                      <div className="text-sm text-muted-foreground">{o.d}</div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-5">
                <div className="font-display text-2xl font-semibold mb-4">Where and how many?</div>
                <div>
                  <Label>Street address *</Label>
                  <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="12 Example St, Bulimba" data-testid="quote-address" />
                </div>
                <div>
                  <Label>Suburb</Label>
                  <Input value={form.suburb} onChange={(e) => set("suburb", e.target.value)} placeholder="Bulimba" data-testid="quote-suburb" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Number of cameras (approx.)</Label>
                    <Input type="number" min={1} max={64} value={form.num_cameras} onChange={(e) => set("num_cameras", parseInt(e.target.value || 1))} data-testid="quote-num-cameras" />
                  </div>
                  <div>
                    <Label>Wired or wireless?</Label>
                    <RadioGroup value={form.installation_type} onValueChange={(v) => set("installation_type", v)} className="flex gap-2 mt-2">
                      {["wired", "wireless", "hybrid"].map(x => (
                        <label key={x} className={`flex-1 py-2.5 text-center border rounded-md text-sm cursor-pointer capitalize transition-colors ${form.installation_type === x ? "bg-eagle-gold text-eagle-ink border-eagle-gold" : "border-border hover:bg-secondary"}`}>
                          <RadioGroupItem value={x} className="sr-only" />{x}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
                <div>
                  <Label>Preferred install date (optional)</Label>
                  <Input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="quote-preferred-date" />
                </div>
                <div>
                  <Label>Notes / requirements</Label>
                  <Textarea rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. Need a camera covering the driveway and back door. Two-story house." data-testid="quote-notes" />
                </div>
              </div>
            )}
            {step === 2 && (
              <div>
                <div className="font-display text-2xl font-semibold mb-2">Upload photos of your property</div>
                <p className="text-sm text-muted-foreground mb-6">Show us the areas you want covered. Up to 10 images. Our team will mark them up with camera placements.</p>
                <label
                  className="flex flex-col items-center justify-center border-2 border-dashed border-border hover:border-eagle-gold rounded-2xl p-10 cursor-pointer transition-colors"
                  data-testid="quote-upload-zone"
                >
                  <Upload className="h-10 w-10 text-muted-foreground mb-3" strokeWidth={1.5} />
                  <div className="font-medium">Click or drop images here</div>
                  <div className="text-xs text-muted-foreground mt-1">JPG / PNG · up to 10 · resized automatically</div>
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid="quote-file-input" />
                </label>
                {form.images.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {form.images.map((src, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border group">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => set("images", form.images.filter((_, j) => j !== i))}
                          className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          data-testid={`quote-remove-image-${i}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 text-xs text-muted-foreground">{form.images.length}/10 images</div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5">
                <div className="font-display text-2xl font-semibold mb-4">How can we reach you?</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Full name *</Label>
                    <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="quote-name" />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="04XX XXX XXX" data-testid="quote-phone" />
                  </div>
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="quote-email" />
                </div>
                <div className="p-4 rounded-lg bg-secondary text-sm text-muted-foreground">
                  We&apos;ll email your quote (with a visual camera plan) within 24 hours. No spam, ever.
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-testid="quote-prev-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button disabled={!canNext()} onClick={() => setStep(s => s + 1)} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover rounded-full px-6" data-testid="quote-next-btn">
              Next <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button disabled={!canNext() || submitting} onClick={submit} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover rounded-full px-6" data-testid="quote-submit-btn">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <>Submit Quote Request <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}